/**
 * Utils Module - Common helper functions
 */

/**
 * Sanitizes a string for use in URLs or attributes
 * @param {string} value - The value to sanitize.
 * @returns {string} The sanitized string containing only alphanumeric characters, hyphens, and underscores.
 */
export function sanitizeUrlParam(value) {
  if (value === undefined || value === null || value === '') return '';
  if (typeof value === 'boolean') return String(value);
  if (!value && value !== 0 && value !== false) return '';
  return String(value).replace(/[^a-zA-Z0-9\-_]/g, '');
}

/**
 * Ensures a path is absolute (prefixed with /)
 * @param {string} path - The relative or absolute path.
 * @returns {string} The formatted absolute path, or a placeholder if empty.
 */
export function getAbsPath(path) {
  if (!path) return '/assets/img/placeholder.webp';
  if (path.startsWith('http') || path.startsWith('/')) return path;
  return '/' + path;
}

/**
 * Escapes HTML to prevent XSS
 * @param {string} text - The raw text that might contain HTML entities.
 * @returns {string} The escaped text safe to be inserted into the DOM.
 */
export function escapeHtml(text) {
  if (!text || typeof text !== 'string') return '';
  const map = {
    '&': '&amp;',
    '<': '&lt;',
    '>': '&gt;',
    '"': '&quot;',
    "'": '&#039;'
  };
  return text.replace(/[&<>"']/g, m => map[m]);
}

/**
 * Generates a news URL based on environment (local vs production)
 * @param {string} slug - The news slug.
 * @param {string} dateSuffix - Optional date suffix (e.g. &date=2026-05-08)
 * @returns {string} The formatted URL.
 */
export function getNewsUrl(slug, dateSuffix = '') {
  if (!slug) return '#';
  
  const hostname = window.location.hostname;
  const isLocal = hostname === 'localhost' || hostname === '127.0.0.1';
  
  if (isLocal) {
    // Local environment needs the ?id= format to work with static server (npx serve)
    return `/noticia?id=${sanitizeUrlParam(slug)}${dateSuffix}`;
  }
  
  // Production environment (Vercel) uses the pretty URL
  // Convert dateSuffix if it exists: &date=... -> ?date=...
  let suffix = dateSuffix;
  if (suffix && suffix.startsWith('&')) {
    suffix = '?' + suffix.substring(1);
  }
  
  return `/py/noticia/${sanitizeUrlParam(slug)}${suffix}`;
}
